/**
 * 流式事件 → 语义化"动作"解释器。
 *
 * 运行场景任务的 jsonl 流本质上是一段描述执行过程的日志，字面形如：
 *   {"type":"log","category":"process","name":"解析故事编写需求","content":"完成初始化"}
 *   {"type":"log","category":"organization","name":"吴磊","content":"符合招募条件"}
 *   {"type":"think","category":"organization","name":"陈杰","content":"[...]","processName":"收集资料"}
 *   {"type":"answer",...}
 *   {"type":"complete",...}
 *
 * 我们把这些文本事件翻译成若干具体的"动作"，由上层组件派发到
 * 过程图 / 组织图 / 候选池 / 结果节点上进行状态更新。
 */

import type { RawStreamEntry, TaskPhase } from '../types';
import { parseSelectedRoles } from './processGraph';

/** 动作类型枚举 */
export type StreamAction =
  /** 进入新的步骤阶段 */
  | {
      kind: 'phase';
      phase: TaskPhase;
      /** running 阶段可能来自“完成全部组队”或后端新增的“开始执行过程”。 */
      source?: 'groupingComplete' | 'explicitProcessStart';
    }
  /** 更新某个过程节点的状态 */
  | {
      kind: 'processStatus';
      name: string;
      status: 'initialized' | 'structured' | 'running' | 'completed' | 'destroyed';
    }
  /** 过程节点选中角色（第三步） */
  | { kind: 'processRoles'; name: string; roles: string[] }
  /** 组织节点状态变化（第二步） */
  | {
      kind: 'orgStatus';
      name: string;
      status: 'applied' | 'approved' | 'rejected';
    }
  /** 人员在第四步开始/结束执行某个过程 */
  | {
      kind: 'personWorkStatus';
      personName: string;
      processName: string;
      isWorking: boolean;
    }
  /** 写入过程节点执行结果（第四步的 think 事件） */
  | { kind: 'processResult'; name?: string; actorName?: string; content: string }
  /** 执行完成 */
  | { kind: 'done' }
  /** 错误 */
  | { kind: 'error'; message: string };

/**
 * 将一条原始日志解释成若干动作。多数情况下仅返回单一动作，
 * 但像"开始构造结构/开始招募"这种既要切阶段又要更新节点的情况会返回多个。
 */
export function interpretEntry(entry: RawStreamEntry): StreamAction[] {
  const actions: StreamAction[] = [];
  const { type, category, content, name } = entry;

  // ==========  系统/通用事件  ==========
  if (type === 'complete') {
    actions.push({ kind: 'phase', phase: 'done' });
    actions.push({ kind: 'done' });
    return actions;
  }
  if (type === 'error') {
    actions.push({ kind: 'error', message: content || '执行发生错误' });
    return actions;
  }

  // ==========  think：仅在运行阶段使用，用于把执行结果落到节点  ==========
  if (type === 'think') {
    actions.push({
      kind: 'processResult',
      // 有 processName 时直接定位；没有时后续由 actorName + 工作态反推。
      name: entry.processName || undefined,
      actorName: name || undefined,
      content,
    });
    return actions;
  }

  // ==========  过程类日志  ==========
  if (category === 'process' && type === 'log') {
    // 末尾解构/销毁阶段不再驱动画布，避免完成后被销毁日志连续触发平移。
    if (
      content === '开始解构结构' ||
      content === '完成销毁' ||
      content === '完成解构结构'
    ) {
      return actions;
    }

    // 阶段切换关键字
    if (content === '开始构造结构') {
      actions.push({ kind: 'phase', phase: 'structuring' });
      return actions;
    }
    if (content === '开始招募') {
      actions.push({ kind: 'phase', phase: 'recruiting' });
      return actions;
    }
    if (content === '完成招募') {
      // 完成招募后进入匹配阶段
      actions.push({ kind: 'phase', phase: 'matching' });
      return actions;
    }
    if (content === '完成全部组队') {
      actions.push({
        kind: 'phase',
        phase: 'running',
        source: 'groupingComplete',
      });
      return actions;
    }
    if (content === '开始执行过程') {
      actions.push({
        kind: 'phase',
        phase: 'running',
        source: 'explicitProcessStart',
      });
      return actions;
    }
    if (content === '完成构造结构') {
      // 根节点完成构造结构：标记自身 structured，并不立刻切阶段（等后端发"开始招募"）
      actions.push({ kind: 'processStatus', name, status: 'structured' });
      return actions;
    }

    // 通用过程节点状态机
    if (content === '完成初始化') {
      actions.push({ kind: 'processStatus', name, status: 'initialized' });
      return actions;
    }
    if (content === '完成结构') {
      actions.push({ kind: 'processStatus', name, status: 'structured' });
      return actions;
    }
    if (content === '完成组队') {
      actions.push({ kind: 'processStatus', name, status: 'structured' });
      return actions;
    }
    if (content.startsWith('选中角色')) {
      const roles = parseSelectedRoles(content);
      actions.push({ kind: 'processRoles', name, roles });
      return actions;
    }
    if (content.startsWith('开始') && content.includes('执行')) {
      actions.push({
        kind: 'processStatus',
        name: entry.processName || name,
        status: 'running',
      });
      return actions;
    }
    if (content.startsWith('完成') && content.includes('执行')) {
      actions.push({
        kind: 'processStatus',
        name: entry.processName || name,
        status: 'completed',
      });
      return actions;
    }
    // "开始运行xxx"/"结束运行xxx"等由 organization 触发的执行轨迹，这里不做处理
    return actions;
  }

  // ==========  组织类日志  ==========
  if (category === 'organization' && type === 'log') {
    if (content === '应募') {
      actions.push({ kind: 'orgStatus', name, status: 'applied' });
      return actions;
    }
    if (content === '符合招募条件') {
      actions.push({ kind: 'orgStatus', name, status: 'approved' });
      return actions;
    }
    if (content === '不符合招募条件') {
      actions.push({ kind: 'orgStatus', name, status: 'rejected' });
      return actions;
    }
    if (content.startsWith('开始运行')) {
      const processName = entry.processName || parseRunningProcessName(content, '开始运行');
      if (!processName) return actions;
      actions.push({
        kind: 'personWorkStatus',
        personName: name,
        processName,
        isWorking: true,
      });
      return actions;
    }
    if (content.startsWith('结束运行')) {
      const processName = entry.processName || parseRunningProcessName(content, '结束运行');
      if (!processName) return actions;
      actions.push({
        kind: 'personWorkStatus',
        personName: name,
        processName,
        isWorking: false,
      });
      return actions;
    }
    // 其他组织日志暂不驱动画布
    return actions;
  }

  return actions;
}

/** 从“开始运行xxx / 结束运行xxx”中提取过程名，兼容旧数据不带 processName 的情况。 */
function parseRunningProcessName(content: string, prefix: string): string {
  return content.slice(prefix.length).trim();
}
